import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Icon } from '@column/column-ui-kit';
import { PageTransferExportModalContent, TransferAmount, transferTableColumns } from '../Transfers/Overview';
import { BankAccountRouteProps } from './Route';
import { TransferRepository, TransferFilterParams, convertTransferAmount } from '~/repositories';
import {
  PopoverFilterAmount,
  PopoverFilterAmountData,
  PopoverFilterTransferTypeData,
  PopoverFilterTransferType,
  PopoverFilterTransferStatus,
  PopoverFilterTransferDate,
  PopoverFilterTransferDateData,
  PopoverFilterWrapper,
} from '~/components';
import { ROUTE } from '~/app/routes';
import { convertDateToRange, downloadBlob, formatNumber, formatNumberText, formatString, getDateFormat } from '~/util';
import { useSessionStore } from '~/stores/Session';
import {
  PaginationWrapperFilter,
  PaginationWrapperFilterCustom,
  PaginationWrapperFilterRefProps,
} from '~/components/PaginationWrapperFilter';
import { AlertData, useAlertStore } from '~/stores/Alert';
import { useNotificationStore } from '~/stores/Notification';
import { useQueryParams } from '~/hooks';

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
  margin-right: 8px;
`;

const FilterLabel = styled.div`
  align-self: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  margin-right: 8px;
`;

const ExportWrapper = styled.div`
  display: flex;
  align-items: end;
  flex-direction: column;
  width: 100%;
`;

export const PageBankAccountsTransfers: React.FC = () => {
  const { openAlert, closeAlert, alertData, updateAlertData } = useAlertStore();
  const { bankAccount } = useOutletContext<BankAccountRouteProps>();
  const navigate = useNavigate();
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const paginationRef = useRef<PaginationWrapperFilterRefProps>(null);

  const { setQueryObject, queryObject } = useQueryParams<TransferFilterParams>();

  const [totalResultsCount, setTotalResultsCount] = useState<number>(0);

  const [amountFilterData, setAmountFilterData] = useState<PopoverFilterAmountData | undefined>({});
  const isAmountFilterActive = useMemo(() => {
    if (
      (amountFilterData?.amount && amountFilterData?.amount > 0) ||
      (amountFilterData?.amountGte && amountFilterData?.amountGte > 0) ||
      (amountFilterData?.amountLte && amountFilterData?.amountLte > 0)
    ) {
      return true;
    }
    return false;
  }, [amountFilterData]);

  const [transferTypeFilterData, setTransferTypeFilterData] = useState<PopoverFilterTransferTypeData>({});
  const isTransferTypeFilterActive = useMemo(() => {
    if (
      (transferTypeFilterData && transferTypeFilterData.type && transferTypeFilterData.type.length > 0) ||
      (transferTypeFilterData && typeof transferTypeFilterData.isIncoming !== 'undefined')
    ) {
      return true;
    }
    return false;
  }, [transferTypeFilterData]);

  const [transferStatusFilterData, setTransferStatusFilterData] = useState<string[] | undefined>([]);

  const [transferDateFilterData, setTransferDateFilterData] = useState<Partial<PopoverFilterTransferDateData>>({});
  const isDateFilterActive = useMemo(() => {
    if (transferDateFilterData?.createdAt || transferDateFilterData?.createdAtTime) {
      return true;
    }
    return false;
  }, [transferDateFilterData]);

  const isFilterActive = useMemo(() => {
    return (
      isAmountFilterActive ||
      isTransferTypeFilterActive ||
      (transferStatusFilterData && transferStatusFilterData.length > 0) ||
      isDateFilterActive
    );
  }, [isAmountFilterActive, transferTypeFilterData, transferStatusFilterData, isDateFilterActive]);

  useEffect(() => {
    const entries = Object.fromEntries(
      Object.entries({
        ...amountFilterData,
        isIncoming: transferTypeFilterData.isIncoming,
        type:
          transferTypeFilterData.type && transferTypeFilterData.type?.length > 0
            ? transferTypeFilterData.type?.join(',')
            : undefined,
        status:
          transferStatusFilterData && transferStatusFilterData?.length > 0
            ? transferStatusFilterData?.join(',')
            : undefined,
        ...convertDateToRange(
          'createdAt',
          transferDateFilterData.createdAt,
          transferDateFilterData.createdAtTime,
          transferDateFilterData.createdAtTimeAM ?? true
        ),
      }).filter(([_, value]) => {
        if (typeof value === 'object' && Object.keys(value).length === 0) {
          return false;
        }
        if (Array.isArray(value) && value.length < 1) {
          return false;
        }
        return typeof value !== 'undefined';
      })
    );

    setQueryObject(() => (Object.keys(entries).length > 0 ? entries : undefined));
  }, [amountFilterData, transferTypeFilterData, transferStatusFilterData, transferDateFilterData]);

  const [fetchFilters, setFetchFilters] = useState<PaginationWrapperFilterCustom | null>({});

  useEffect(() => {
    if (typeof queryObject === 'undefined') {
      setFetchFilters(null);
      return;
    }
    const { amount, amountGte, amountLte, isIncoming, type, status, createdAt } = queryObject;

    setAmountFilterData({
      amount: amount ? Number(amount) : undefined,
      amountGte: amountGte ? Number(amountGte) : undefined,
      amountLte: amountLte ? Number(amountLte) : undefined,
    });

    setTransferTypeFilterData({
      isIncoming: typeof isIncoming !== 'undefined' ? String(isIncoming) === 'true' : undefined,
      type: type ? (type.split(',') as any) : [],
    });

    setTransferStatusFilterData(status ? status.split(',') : []);

    setTransferDateFilterData({
      createdAt: createdAt ? String(createdAt) : undefined,
    });

    setFetchFilters(queryObject);
  }, [queryObject]);

  const handleFilterReset = useCallback(() => {
    setAmountFilterData({});
    setTransferTypeFilterData({});
    setTransferStatusFilterData([]);
    setTransferDateFilterData({});
    setFetchFilters(null);
  }, []);

  const [exportAnimate, setExportAnimate] = useState<boolean>(false);

  const handleExport = (dataAlert?: AlertData) => {
    if (alertData?.submitLoading) {
      return;
    }

    updateAlertData({ submitLoading: true });
    setExportAnimate(true);

    TransferRepository.export({ ...queryObject, bankAccountId: bankAccount.id })
      .then((response) => {
        downloadBlob(
          response,
          `${
            dataAlert?.customFileName && dataAlert.customFileName.length > 0
              ? dataAlert.customFileName
              : `transfers-${getDateFormat(new Date())}`
          }.csv`
        );
      })
      .then(() => {
        closeAlert();
      })
      .catch((e) =>
        addDangerNotification({
          content: e.message,
          display: 'popup',
        })
      )
      .finally(() => {
        updateAlertData({ submitLoading: false });
      });
  };

  const handleExportClick = () => {
    let text = `Do you want to export & download the .csv file for ${formatNumberText(totalResultsCount)} transfers?`;

    if (!isFilterActive) {
      text = `Are you sure you want to export & download the .csv file for all transfers (${formatNumberText(
        totalResultsCount
      )}) without any filters applied?`;
    }

    if (totalResultsCount > 1000) {
      text = `Are you sure to export & download the .csv file for ${formatNumberText(
        totalResultsCount
      )} transfers? This may take a while.`;
    }

    openAlert(
      {
        headline: 'Export',
        text: <PageTransferExportModalContent text={text} />,
        submitText: 'Download',
        callback: handleExport,
        type: 'info',
        icon: <Icon.AnimationDownload running={exportAnimate} onDone={() => setExportAnimate(false)} />,
      },
      {
        submitStay: true,
        closeTimeoutMs: 500,
      }
    );
  };

  const entryObject = (entry: any, type?: string) => {
    const object: any = {};

    if (entry.type) {
      object.type =
        entry.type === 'ach_debit'
          ? 'ACH Debit'
          : entry.type === 'ach_credit'
          ? 'ACH Credit'
          : entry.type === 'check_debit' || entry.type === 'check_credit'
          ? 'Check'
          : entry.type === 'swift'
          ? 'Intl. Wire'
          : formatString(entry.type);
    }

    if (type) {
      object.type = type;
    }

    const convertAmount = convertTransferAmount(entry.type, entry.isIncoming);

    return {
      id: entry.id,
      ...object,
      description: [
        <TransferAmount key={entry.id} $isAdding={entry.type === 'book' ? undefined : convertAmount !== -1}>
          {formatNumber(entry?.amount ?? 0)}
        </TransferAmount>,
        entry.description,
      ],
      status: entry.status,
      incoming:
        entry.type === 'check_credit'
          ? 'Deposit'
          : entry.type === 'check_debit'
          ? 'Issue'
          : entry.isIncoming
          ? 'Incoming'
          : 'Outgoing',
      created: entry.createdAt,
    };
  };

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
        () => {
          handleFilterReset();
        },
        {
          fireImmediately: true,
        }
      ),
    []
  );

  const handleFetch = useCallback(async (params: TransferFilterParams) => {
    return TransferRepository.getAll({ ...params, bankAccountId: bankAccount.id }).then((response) => {
      const entries: any = [];

      if (response.transfers.length) {
        response.transfers.map((entry: any) => {
          entries.push(entryObject(entry));
        });
      }

      setTotalResultsCount(response.totalResultsCount);

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  }, []);

  return (
    <PaginationWrapperFilter
      tableId="transfers"
      ref={paginationRef}
      fetch={handleFetch}
      fetchFilter={fetchFilters}
      columns={transferTableColumns}
      action={
        <>
          {isFilterActive ? (
            <ResetFilter
              size="small"
              variant="subtle-primary"
              icon={<Icon.Reset />}
              iconRight
              onClick={handleFilterReset}
            >
              Reset filters
            </ResetFilter>
          ) : (
            <FilterLabel>Filter</FilterLabel>
          )}
          <PopoverFilterWrapper label="Amount" onSubmit={setAmountFilterData} isActive={isAmountFilterActive}>
            <PopoverFilterAmount data={amountFilterData ?? {}} />
          </PopoverFilterWrapper>
          <PopoverFilterWrapper label="Type" onSubmit={setTransferTypeFilterData} isActive={isTransferTypeFilterActive}>
            <PopoverFilterTransferType data={transferTypeFilterData ?? {}} />
          </PopoverFilterWrapper>
          <PopoverFilterWrapper
            label="Status"
            onSubmit={setTransferStatusFilterData}
            isActive={transferStatusFilterData && transferStatusFilterData.length > 0}
          >
            <PopoverFilterTransferStatus data={transferStatusFilterData ?? []} />
          </PopoverFilterWrapper>
          <PopoverFilterWrapper label="Date" onSubmit={setTransferDateFilterData} isActive={isDateFilterActive}>
            <PopoverFilterTransferDate data={transferDateFilterData ?? []} />
          </PopoverFilterWrapper>
          <ExportWrapper>
            <Button
              icon={<Icon.AnimationDownload running={exportAnimate} onDone={() => setExportAnimate(false)} />}
              size="small"
              variant="secondary"
              onClick={handleExportClick}
            >
              Export
            </Button>
          </ExportWrapper>
        </>
      }
      rowClick={(row) => {
        navigate(
          `${ROUTE.TRANSFERS}/edit/${(row.original.type || 'ach')
            .toString()
            .toLowerCase()
            .replace('debit', '')
            .replace('credit', '')
            .replace('. ', '-')
            .trim()}/${row.original.id}`
        );
      }}
    />
  );
};
